/* eslint-disable import/no-cycle */
import { createSelector } from 'reselect';
import {
  filter,
  find,
  has,
  path,
  pipe,
  propEq,
  propOr,
  prop,
  pathOr,
  concat,
  compose,
  map,
  prepend,
  either,
  pathEq,
} from 'ramda';
import moment from 'moment';

import { getFormattedTelecom, formatDate, getAgeFromDobOr } from 'utilities/formatters';
import i18n from 'services/i18n';
import { NA } from 'constants/common';
import { isTechnician } from 'store/selectors/user';
import {
  getPatientQuestionnaireResponseDeviceClinicSpecial,
  getPatientQuestionnaireResponseSatelliteDeviceClinic,
  getPatientQuestionnaireResponseLocalDeviceClinicPhone,
} from 'store/selectors/questionnaireResponses';

const localState = prop('patientsData');

export const getPatientById = id =>
  createSelector(
    localState,
    prop(id),
  );

export const getPatientLoadingStatus = id =>
  createSelector(
    getPatientById(id),
    propOr(true, 'loading'),
  );

export const getPatientUpdateLoadingStatus = id =>
  createSelector(
    getPatientById(id),
    propOr(false, 'updateLoading'),
  );

export const getPatientData = id =>
  createSelector(
    getPatientById(id),
    prop('data'),
  );

export const getPatientShouldShowLoading = id =>
  createSelector(
    [getPatientLoadingStatus(id), getPatientData(id)],
    (loading, data) => loading && !data,
  );

export const getPatientName = id =>
  createSelector(
    getPatientById(id),
    path(['data', 'name', '0', 'text']),
  );

export const getPatientId = id =>
  createSelector(
    getPatientById(id),
    path(['data', 'id']),
  );

export const isUseSms = id =>
  createSelector(
    getPatientData(id),
    pipe(
      propOr([], 'extension'),
      find(propEq('url', 'contact.viaSms')),
      propOr('', 'valueCode'),
    ),
  );

export const isUseEmail = id =>
  createSelector(
    getPatientData(id),
    pipe(
      propOr([], 'extension'),
      find(propEq('url', 'contact.emailNotification')),
      propOr('', 'valueCode'),
    ),
  );

export const getPatientTelecom = id =>
  createSelector(
    getPatientById(id),
    pipe(
      pathOr([], ['data', 'telecom']),
      filter(has('value')),
    ),
  );

export const getPatientPhone = id =>
  createSelector(
    getPatientTelecom(id),
    pipe(
      find(propEq('system', 'phone')),
      prop('value'),
    ),
  );

export const getUserProfileCreatedOn = id =>
  createSelector(
    getPatientData(id),
    pipe(
      propOr([], 'extension'),
      find(propEq('url', 'created-on')),
      propOr(null, 'valueDateTime'),
    ),
  );

export const getUserProfileLanguage = id =>
  createSelector(
    getPatientData(id),
    pathOr(null, ['communication', '0', 'language', 'text']),
  );

export const getUserProfileLangCode = id =>
  createSelector(
    getPatientData(id),
    pathOr(null, ['communication', '0', 'language', 'coding', '0', 'code']),
  );

export const getUserProfileInfoDataList = id =>
  createSelector(
    getPatientData(id),
    data => [
      {
        value: `RPM-CIED-${propOr(null, 'id')(data)}`,
      },
      {
        name: 'patient.age',
        value: getAgeFromDobOr(NA, prop('birthDate', data)),
      },
      {
        name: 'patient.sex',
        value: prop('gender')(data) ? i18n.t(prop('gender')(data)) : NA,
      },
      {
        name: 'patient.language',
        value: pathOr(null, ['communication', '0', 'language', 'text'])(data),
      },
      {
        name: 'patient.deceased',
        value: pipe(
          propOr([], 'extension'),
          find(propEq('url', 'dodeath')),
          propOr(null, 'valueDate'),
        )(data),
      },
      {
        name: 'patient.eightTeenMonthVisit',
        value: pipe(
          propOr([], 'extension'),
          find(propEq('url', '18_month_followup')),
          propOr(null, 'valueCode'),
        )(data),
      },
    ],
  );

export const getUserProfileInfoForTechnician = id =>
  createSelector(
    getUserProfileInfoDataList(id),
    isTechnician,
    getPatientId(id),
    (data, isDt, patientId) =>
      isDt
        ? data
        : [
            {
              value: `RPM-CIED-${patientId}`,
            },
          ],
  );

export const getPatientContact = id =>
  createSelector(
    getPatientById(id),
    pathOr([], ['data', 'contact']),
  );

export const getResearchersAndDoctors = id =>
  createSelector(
    getPatientData(id),
    pipe(
      pathOr([], ['generalPractitioner']),
      filter(either(pathEq(['identifier', 'value'], 'D'), pathEq(['identifier', 'value'], 'RC'))),
    ),
  );

export const getLocalClinicInfo = id =>
  createSelector(
    getPatientQuestionnaireResponseDeviceClinicSpecial(id),
    getPatientQuestionnaireResponseSatelliteDeviceClinic(id),
    getPatientQuestionnaireResponseLocalDeviceClinicPhone(id),
    (clinicNameSpecial, clinicName, clinicPhone) => [
      { title: 'patient.clinicName1', value: clinicNameSpecial },
      { title: 'patient.clinicName2', value: clinicName },
      { title: 'patient.localClinicPhone', value: clinicPhone },
    ],
  );

export const getSidebarPatientsInfoList = id =>
  createSelector(
    getPatientTelecom(id),
    getPatientContact(id),
    getUserProfileCreatedOn(id),
    getLocalClinicInfo(id),
    (telecom, contact, createdData, clinicData) =>
      compose(
        concat([
          {
            title: 'patient.personalContactDetails',
            items: getFormattedTelecom(telecom),
          },
        ]),
        concat(
          map(
            ({ name, relationship, telecom: contactTelecom }) => ({
              title: i18n.t(path(['0', 'coding', '0', 'display'], relationship)),
              items: compose(
                prepend({ value: name.text }),
                concat(getFormattedTelecom(contactTelecom)),
              )([]),
            }),
            contact,
          ),
        ),
        prepend({
          title: 'patient.profileCreatedOn',
          items: [
            {
              value: formatDate(i18n.t('dateTimeFormat.profileCreatedOn'))(createdData),
            },
          ],
        }),
        prepend({
          title: 'patient.localClinicInfo',
          items: clinicData,
        }),
      )([]),
  );

export const getPatientDeviceType = id =>
  createSelector(
    getPatientData(id),
    pipe(
      propOr([], 'extension'),
      find(propEq('url', 'device.type')),
      propOr('', 'valueCode'),
    ),
  );

export const getPatientDeviceName = id =>
  createSelector(
    getPatientData(id),
    pipe(
      propOr([], 'extension'),
      find(propEq('url', 'device.name')),
      propOr('', 'valueCode'),
    ),
  );

export const getPatientBirthDate = id =>
  createSelector(
    getPatientData(id),
    propOr('', 'birthDate'),
  );

export const getPatientYO = id =>
  createSelector(
    getPatientBirthDate(id),
    birthDate =>
      birthDate
        ? i18n.t('dateTimeFormat.patientYears', {
            years: moment().diff(birthDate, 'years'),
          })
        : '',
  );

export const getPatientGender = id =>
  createSelector(
    getPatientData(id),
    pipe(
      prop('gender'),
      gender => (gender ? i18n.t(gender) : ''),
    ),
  );
